import React, {useEffect, useState, useRef} from 'react';
import {Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography} from "@mui/material";
import {Input} from "reactstrap";
import {useSelector} from 'react-redux'

export const PANCANCalculatorDialog = ({open, onClose}) => {
    const prevOpenRef = useRef(open);
    const attemptInfo = useSelector(state => state.testView.attemptInfo);
    const [ageValue, setAgeValue] = useState('');
    const [ageUnitValue, setAgeUnitValue] = useState('1|0|years');
    const [sexValue, setSexValue] = useState('');
    const [familyHistoryValue, setFamilyHistoryValue] = useState(false);
    const [emphysemaValue, setEmphysemaValue] = useState(false);
    const [noduleSizeValue, setNoduleSizeValue] = useState('');
    const [noduleSizeUnitValue, setNoduleSizeUnitValue] = useState('1|0|mm');
    const [noduleTypeValue, setNoduleTypeValue] = useState('');
    const [noduleUpperLungValue, setNoduleUpperLungValue] = useState(false);
    const [noduleCountValue, setNoduleCountValue] = useState('');
    const [noduleCountUnitValue, setNoduleCountUnitValue] = useState('1|0|#');
    const [spiculationValue, setSpiculationValue] = useState(false);
    const [decimalPrecisionValue, setDecimalPrecisionValue] = useState('2');
    const [logOddsValue, setLogOddsValue] = useState('');
    const [cancerProbabilityValue, setCancerProbabilityValue] = useState('');
    const [cancerProbabilityUnitValue, setCancerProbabilityUnitValue] = useState('1|0|%');

    useEffect(() => {
        calculateScore();
    }, [
        ageValue,
        ageUnitValue,
        sexValue,
        familyHistoryValue,
        emphysemaValue,
        noduleSizeValue,
        noduleSizeUnitValue,
        noduleTypeValue,
        noduleUpperLungValue,
        noduleCountValue,
        noduleCountUnitValue,
        spiculationValue,
        decimalPrecisionValue,
        logOddsValue,
        cancerProbabilityValue,
        cancerProbabilityUnitValue,
    ]);

    useEffect(() => {
        const sessionStorageKey = `pancan-${attemptInfo.id.substring(0, 8)}`;
        if (open) {
            const previousValues = JSON.parse(sessionStorage.getItem(sessionStorageKey) || '{}');
            setAgeValue(previousValues.ageValue || '');
            setAgeUnitValue(previousValues.ageUnitValue || '1|0|years');
            setSexValue(previousValues.sexValue || '');
            setFamilyHistoryValue(previousValues.familyHistoryValue || false);
            setEmphysemaValue(previousValues.emphysemaValue || false);
            setNoduleSizeValue(previousValues.noduleSizeValue || '');
            setNoduleSizeUnitValue(previousValues.noduleSizeUnitValue || '1|0|mm');
            setNoduleTypeValue(previousValues.noduleTypeValue || '');
            setNoduleUpperLungValue(previousValues.noduleUpperLungValue || false);
            setNoduleCountValue(previousValues.noduleCountValue || '');
            setNoduleCountUnitValue(previousValues.noduleCountUnitValue || '1|0|#');
            setSpiculationValue(previousValues.spiculationValue || false);
            setDecimalPrecisionValue(previousValues.decimalPrecisionValue || '2');
            setCancerProbabilityUnitValue(previousValues.cancerProbabilityUnitValue || '1|0|%');
        } else if (prevOpenRef.current && !open) {
            sessionStorage.setItem(sessionStorageKey, JSON.stringify({
                ageValue,
                ageUnitValue,
                sexValue,
                familyHistoryValue,
                emphysemaValue,
                noduleSizeValue,
                noduleSizeUnitValue,
                noduleTypeValue,
                noduleUpperLungValue,
                noduleCountValue,
                noduleCountUnitValue,
                spiculationValue,
                decimalPrecisionValue,
                cancerProbabilityUnitValue,
            }));
        }
        prevOpenRef.current = open;
    }, [open]);

    const onAgeBlue = () => {
        let error;
        if (isNaN(ageValue)) {
            error = "This field is improperly formatted\n\nYou may only input the digits 0-9 and a decimal point";
        }
        const age = parseFloat(ageValue) * parseFloat(ageUnitValue.split('|')[0]) + parseFloat(ageUnitValue.split('|')[1]);
        if (age < 18) {
            error = 'The minimum value for Age is 18 years.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (age > 120) {
            error = 'The maximum value for Age is 120 years.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (error) {
            alert(error);
            setAgeValue('');
        }
    }

    const onNoduleSizeBlue = () => {
        let error;
        if (isNaN(noduleSizeValue)) {
            error = 'This field is improperly formatted\n\nYou may only input the digits 0-9 and a decimal point';
        }
        const noduleSize = parseFloat(noduleSizeValue) * parseFloat(noduleSizeUnitValue.split('|')[0]) + parseFloat(noduleSizeUnitValue.split('|')[1]);
        if (noduleSize < 1) {
            error = 'The minimum value for Nodule size is 1 mm.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (noduleSize > 30) {
            error = 'The maximum value for Nodule size is 30 mm.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (error) {
            alert(error);
            setNoduleSizeValue('');
        }
    }

    const onNoduleCountBlue = () => {
        let error;
        if (isNaN(noduleCountValue)) {
            error = 'This field is improperly formatted\n\nYou may only input the digits 0-9 and a decimal point';
        }
        const noduleCount = parseFloat(noduleCountValue) * parseFloat(noduleCountUnitValue.split('|')[0]) + parseFloat(noduleCountUnitValue.split('|')[1]);
        if (noduleCount < 1) {
            error = 'The minimum value for Nodule count is 1 #.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (noduleCount > 100) {
            error = 'The maximum value for Nodule count is 100 #.\n\nIf you are specifying a value with a different unit, change the unit selector first.';
        }
        if (error) {
            alert(error);
            setNoduleCountValue('');
        }
    }

    const calculateScore = () => {
        if (!ageValue || !sexValue || !noduleSizeValue || !noduleTypeValue || !noduleCountValue) {
            setLogOddsValue('');
            setCancerProbabilityValue('');
            return;
        }
        const age = parseFloat(ageValue) * parseFloat(ageUnitValue.split('|')[0]) + parseFloat(ageUnitValue.split('|')[1]);
        const sex = parseFloat(sexValue.split('|')[1]);
        const familyHistory = familyHistoryValue ? 0.2961 : 0;
        const emphysema = emphysemaValue ? 0.2953 : 0;
        const noduleSize = parseFloat(noduleSizeValue) * parseFloat(noduleSizeUnitValue.split('|')[0]) + parseFloat(noduleSizeUnitValue.split('|')[1]);
        const noduleType = parseFloat(noduleTypeValue.split('|')[1]);
        const noduleUpperLung = noduleUpperLungValue ? 0.6581 : 0;
        const noduleCount = parseFloat(noduleCountValue) * parseFloat(noduleCountUnitValue.split('|')[0]) + parseFloat(noduleCountUnitValue.split('|')[1]);
        const spiculation = spiculationValue ? 0.7729 : 0;
        const decimalPrecision = parseInt(decimalPrecisionValue, 10);
        const logOdds = (0.0287 * (age - 62)) + sex + familyHistory + emphysema - (5.3854 * (Math.pow((noduleSize / 10), -0.5) - 1.58113883)) + noduleType + noduleUpperLung - (0.0824 * (noduleCount - 4)) + spiculation - 6.7892;
        setLogOddsValue(logOdds.toFixed(decimalPrecision));
        const cancerProbability = 100 * (Math.exp(logOdds) / (1 + Math.exp(logOdds)));
        const unitParts = cancerProbabilityUnitValue.split('|');
        setCancerProbabilityValue(((cancerProbability - parseFloat(unitParts[1])) / parseFloat(unitParts[0])).toFixed(decimalPrecision));
    }

    return (
        <Dialog maxWidth={'md'} open={open} onClose={onClose}>
            <DialogTitle>
                PANCAN Calculator
            </DialogTitle>
            <DialogContent>
                <div className={'pancan-calculator-container'}>
                    <div>
                        <span>Age</span>
                        <Input type={'number'} value={ageValue} onChange={(e) => setAgeValue(e.target.value)} onBlur={onAgeBlue}/>
                        <Input type="select" value={ageUnitValue} onChange={(e) => setAgeUnitValue(e.target.value)} onBlur={onAgeBlue}>
                            <option value="0.0833333333333333|0|months">months</option>
                            <option value="1|0|years">years</option>
                        </Input>
                    </div>
                    <div>
                        <span>Sex</span>
                        <div>
                            <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                defaultValue="female"
                                name="radio-buttons-group"
                                value={sexValue}
                                onChange={e => setSexValue(e.target.value)}
                            >
                                <FormControlLabel value="Female|0.6011" control={<Radio/>} label="Female (0.6011)"/>
                                <FormControlLabel value="Male|0" control={<Radio/>} label="Male (0)"/>
                            </RadioGroup>
                        </div>
                    </div>
                    <div>
                        <span>Family history of lung cancer</span>
                        <FormControlLabel
                            label="(0.2961)"
                            control={<Checkbox checked={familyHistoryValue} onChange={(e) => setFamilyHistoryValue(e.target.checked)}/>}
                        />
                    </div>
                    <div>
                        <span>Emphysema</span>
                        <FormControlLabel
                            label="(0.2953)"
                            control={<Checkbox checked={emphysemaValue} onChange={(e) => setEmphysemaValue(e.target.checked)}/>}
                        />
                    </div>
                    <div>
                        <span>Nodule size</span>
                        <Input type={'number'} value={noduleSizeValue} onChange={(e) => setNoduleSizeValue(e.target.value)} onBlur={onNoduleSizeBlue}/>
                        <Input type="select" value={noduleSizeUnitValue} onChange={(e) => setNoduleSizeUnitValue(e.target.value)} onBlur={onNoduleSizeBlue}>
                            <option value="10|0|cm">cm</option>
                            <option value="304.8|0|ft">ft</option>
                            <option value="25.4|0|in">in</option>
                            <option value="0.001|0|micm">micm</option>
                            <option value="1|0|mm">mm</option>
                            <option value="1000|0|m">m</option>
                            <option value="1e-06|0|nm">nm</option>
                            <option value="914.4|0|yd">yd</option>
                        </Input>
                    </div>
                    <div>
                        <span>Nodule type</span>
                        <div>
                            <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                defaultValue="female"
                                name="radio-buttons-group"
                                value={noduleTypeValue}
                                onChange={e => setNoduleTypeValue(e.target.value)}
                            >
                                <FormControlLabel value="Nonsolid or ground-glass|-0.1276" control={<Radio/>} label="Nonsolid or ground-glass (-0.1276)"/>
                                <FormControlLabel value="Partially solid|0.377" control={<Radio/>} label="Partially solid (0.377)"/>
                                <FormControlLabel value="Solid|0" control={<Radio/>} label="Solid (0)"/>
                            </RadioGroup>
                        </div>
                    </div>
                    <div>
                        <span>Nodule in upper lung</span>
                        <FormControlLabel
                            label="(0.6581)"
                            control={<Checkbox checked={noduleUpperLungValue} onChange={(e) => setNoduleUpperLungValue(e.target.checked)}/>}
                        />
                    </div>
                    <div>
                        <span>Nodule count</span>
                        <Input type={'number'} value={noduleCountValue} onChange={(e) => setNoduleCountValue(e.target.value)} onBlur={onNoduleCountBlue}/>
                        <Input type="select" value={noduleCountUnitValue} onChange={(e) => setNoduleCountUnitValue(e.target.value)} onBlur={onNoduleCountBlue}>
                            <option value="1|0|#">#</option>
                            <option value="1|0|index">index</option>
                            <option value="1|0|none">none</option>
                            <option value="1|0|number">number</option>
                            <option value="1|0|points">points</option>
                            <option value="1|0|score">score</option>
                        </Input>
                    </div>
                    <div>
                        <span>Spiculation</span>
                        <FormControlLabel
                            label="(0.7729)"
                            control={<Checkbox checked={spiculationValue} onChange={(e) => setSpiculationValue(e.target.checked)}/>}
                        />
                    </div>
                    <Typography variant={'h6'}>Result</Typography>
                    <div>
                        <span>Log odds</span>
                        <Input type={'number'} value={logOddsValue} disabled/>
                    </div>
                    <div>
                        <span>Cancer probability</span>
                        <Input type={'number'} value={cancerProbabilityValue} disabled/>
                        <Input type="select" value={cancerProbabilityUnitValue} onChange={(e) => setCancerProbabilityUnitValue(e.target.value)}>
                            <option value="1|0|%">%</option>
                            <option value="100|0|fraction">fraction</option>
                            <option value="100|0|ratio">ratio</option>
                        </Input>
                    </div>
                    <div>
                        <span>Decimal precision</span>
                        <Input type="select" value={decimalPrecisionValue} onChange={(e) => setDecimalPrecisionValue(e.target.value)}>
                            <option>0</option>
                            <option>1</option>
                            <option>2</option>
                            <option>3</option>
                        </Input>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}